/**
 * Roll call: set each person's state for one session, and write down what happened.
 *
 * This is the screen used while standing in front of a group, so it is built around large
 * targets, one tap per person, and no scrolling to reach the controls. Every tap writes to
 * the local replica immediately; the network is never on the critical path.
 *
 * A session in the future only offers "excused": attendance cannot be observed before it
 * happens, but an absence announced in advance can be.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { dayNote, personNote } from '../../shared/notes.js';
import { nearestSession, nextSession, previousSession } from '../../shared/schedule.js';
import { addState, emptyCounts, stateAt, type StateCounts } from '../../shared/stats.js';
import { AttendanceState, type IsoDate, type Person, type PersonId } from '../../shared/types.js';
import { setMark, setMarksForAll, setNote } from '../data/mutations.js';
import { useStore } from '../data/context.js';
import { useProjectDerived } from '../data/derived.js';
import { useRemoteEdits } from '../data/selectors.js';
import type { MarkIndex } from '../../shared/stats.js';
import { FUTURE_CYCLES, RECORDING_CYCLES, StateChoice, StateFigures, appearanceOf } from '../ui/attendance.js';
import { SessionSpark } from '../ui/charts/SessionSpark.js';
import { StatusBar } from '../ui/charts/StatusBar.js';
import { formatClock, formatLongDate, formatMediumDate, formatSessionDate, plural, relativeDayLabel } from '../ui/format.js';
import { ChevronLeftIcon, ChevronRightIcon, CommentIcon, PeopleIcon } from '../ui/icons.js';
import { NoteBlock, NoteButton, NoteForm } from '../ui/notes.js';
import { Button, EmptyState, IconButton } from '../ui/primitives.js';
import { ProjectFrame } from '../ui/Shell.js';

/** Roster size above which a filter box is worth the space it takes. */
const FILTER_THRESHOLD = 12;

/** How many recent sessions the comparison strip shows. */
const SPARK_SESSIONS = 12;

/** Counts the states of `people` on `date`. */
function countStates(people: readonly Person[], date: IsoDate, marks: MarkIndex): StateCounts {
  const counts = emptyCounts();
  for (const person of people) addState(counts, stateAt(marks, person.id, date));
  return counts;
}

/** Renders the roll-call screen for the selected session. */
export function RollCallPage(): ReactNode {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const store = useStore();
  const derived = useProjectDerived(projectId);
  const [chosen, setChosen] = useState<IsoDate | null>(null);
  const [filter, setFilter] = useState('');
  const [openNote, setOpenNote] = useState<PersonId | null>(null);
  const sessions = derived?.allSessions ?? [];
  const day = derived?.day ?? '';
  // `?date=` opens one session directly, which is how the statistics link back to a day. It
  // only holds until the arrows are used; after that the chosen date wins.
  const requested = searchParams.get('date');
  const linked = requested !== null && sessions.includes(requested) ? requested : null;
  const openDate = chosen ?? linked ?? (derived === null ? null : nearestSession(sessions, day));
  const remoteEdits = useRemoteEdits(projectId, openDate);

  const date = openDate;

  const visible = useMemo(() => {
    const roster = derived?.people ?? [];
    const needle = filter.trim().toLowerCase();
    if (needle === '') return roster;
    return roster.filter((person) => person.name.toLowerCase().includes(needle));
  }, [derived, filter]);

  const counts = useMemo(
    () => (derived === null || date === null ? emptyCounts() : countStates(derived.people, date, derived.marks)),
    [derived, date],
  );

  const recentTallies = useMemo(
    () => (derived === null ? [] : derived.sessionTallies.slice(-SPARK_SESSIONS)),
    [derived],
  );

  if (derived === null) {
    return <ProjectFrame><EmptyState message="This project is not on this device." /></ProjectFrame>;
  }

  const { project, people, marks, notes } = derived;

  if (date === null) {
    return (
      <ProjectFrame>
        <EmptyState
          message="No session days are set yet. Choose the weekdays you meet, or pick individual dates."
          action={<Link to={`/p/${project.id}/calendar`}><Button tone="primary">Open the calendar</Button></Link>}
        />
      </ProjectFrame>
    );
  }

  const previous = previousSession(sessions, date);
  const upcoming = nextSession(sessions, date);
  const isFuture = date > day;
  const isToday = date === day;
  const relative = relativeDayLabel(date, day);
  const unsetIds = people
    .filter((person) => stateAt(marks, person.id, date) === undefined)
    .map((person) => person.id);
  const cycles = isFuture ? FUTURE_CYCLES : RECORDING_CYCLES;

  return (
    <ProjectFrame>
      {/* The session and its figures lead; the roster below is the surface worked on. Keeping
          this band on the page colour and the rows on the lighter surface separates the two
          without a box around either. */}
      <section className="border-b border-line px-4 pt-2 pb-4">
        <div className="flex items-center justify-between gap-2">
          <IconButton
            label={previous === null ? 'No earlier session' : `Earlier session, ${formatMediumDate(previous)}`}
            disabled={previous === null}
            onClick={() => previous !== null && setChosen(previous)}
          >
            <ChevronLeftIcon size={24} />
          </IconButton>

          <div className="min-w-0 text-center">
            <p className="truncate text-title leading-tight font-bold tracking-tight" title={formatLongDate(date)}>
              <span className="sm:hidden">{formatSessionDate(date, day)}</span>
              <span className="hidden sm:inline">{formatLongDate(date)}</span>
            </p>
            {isToday ? (
              <p className="mt-0.5 text-tiny font-semibold text-accent-text">Today</p>
            ) : (
              <p className="mt-0.5 flex items-center justify-center gap-2 text-tiny">
                <span className="text-ink-3">{relative ?? formatMediumDate(date)}</span>
                <button
                  type="button"
                  className="font-semibold text-brand-text underline-offset-2 hover:underline"
                  onClick={() => setChosen(nearestSession(sessions, day))}
                >
                  Back to today
                </button>
              </p>
            )}
          </div>

          <IconButton
            label={upcoming === null ? 'No later session' : `Later session, ${formatMediumDate(upcoming)}`}
            disabled={upcoming === null}
            onClick={() => upcoming !== null && setChosen(upcoming)}
          >
            <ChevronRightIcon size={24} />
          </IconButton>
        </div>

        <div className="mt-4">
          <StatusBar counts={counts} height={10} />
        </div>
        {/* The bar shows the proportions; these are the figures behind it, including the one
            the bar cannot act on — how many rows are still untouched. */}
        <div className="mt-4">
          <StateFigures counts={counts} />
        </div>

        {isFuture || unsetIds.length === 0 ? null : (
          <button
            type="button"
            className="mt-3.5 text-tiny font-semibold text-brand-text underline-offset-2 hover:underline"
            onClick={() => void setMarksForAll(store, project.id, unsetIds, date, AttendanceState.Attending)}
          >
            Mark the remaining {unsetIds.length} attending
          </button>
        )}

        <SessionSpark tallies={recentTallies} selected={date} onSelect={setChosen} />
      </section>

      <section className="border-b border-line px-4 py-1">
        <NoteBlock
          label={`Note about the session on ${formatMediumDate(date)}`}
          addLabel="Write a note about this session"
          placeholder="What happened today — a trip, a test, a room change."
          note={dayNote(notes, date)}
          onSave={(text) => void setNote(store, project.id, date, null, text)}
        />
      </section>

      {remoteEdits.author === null ? null : (
        <p className="flex items-center gap-2 border-b border-line bg-accent-soft px-4 py-2.5 text-tiny text-accent-ink">
          <PeopleIcon size={16} />
          <span>
            <span className="font-semibold">{remoteEdits.author}</span>
            {' '}changed {remoteEdits.count} {plural(remoteEdits.count, 'mark', 'marks')} in this
            session, last at {formatClock(remoteEdits.at)}.
          </span>
        </p>
      )}

      {people.length === 0 ? (
        <EmptyState
          message="Nobody is on this roster yet."
          action={<Link to={`/p/${project.id}/people`}><Button tone="primary">Add people</Button></Link>}
        />
      ) : (
        <>
          {people.length > FILTER_THRESHOLD ? (
            <div className="px-4 pt-3 pb-3">
              <input
                type="search"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder="Find a name"
                aria-label="Filter the roster by name"
                className="h-11 w-full rounded-control border border-line-strong bg-surface px-3 text-base"
              />
            </div>
          ) : null}

          <ul className="border-t border-line">
            {visible.map((person) => {
              const state = stateAt(marks, person.id, date);
              const own = personNote(notes, project.id, person.id, date);
              const editing = openNote === person.id;
              const appearance = appearanceOf(state ?? 'unset');
              return (
                <li key={person.id} className="border-b border-line bg-surface">
                  <div className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-sunken">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/p/${project.id}/people/${person.id}`}
                        className="block truncate text-row leading-snug font-medium text-ink hover:text-brand-text"
                      >
                        {person.name}
                      </Link>
                      {person.note === '' ? null : (
                        <p className="truncate text-tiny leading-snug text-ink-3">{person.note}</p>
                      )}
                      {own === undefined ? null : (
                        <p className="flex items-center gap-1.5 text-tiny leading-snug text-ink-2">
                          <CommentIcon size={13} className="shrink-0 text-ink-3" />
                          <span className="truncate">{own.text}</span>
                        </p>
                      )}
                    </div>
                    {/* Fixed width and no wrapping, so a long word such as "not recorded"
                        neither wraps to a second line nor shifts the buttons row to row. */}
                    <span className={`hidden w-24 shrink-0 text-right text-tiny font-semibold
                      whitespace-nowrap sm:inline ${appearance.text}`}>
                      {appearance.label}
                    </span>
                    <StateChoice
                      groupLabel={`Attendance for ${person.name}`}
                      current={state}
                      cycles={cycles}
                      onSet={(next) => void setMark(store, project.id, person.id, date, next)}
                    />
                    <NoteButton
                      label={`${person.name} on this session`}
                      hasNote={own !== undefined}
                      expanded={editing}
                      onClick={() => setOpenNote(editing ? null : person.id)}
                    />
                  </div>
                  {editing ? (
                    <div className="border-t border-line px-4 py-3">
                      <NoteForm
                        label={`Note about ${person.name} on ${formatMediumDate(date)}`}
                        value={own?.text ?? ''}
                        placeholder={`Anything worth remembering about ${person.name} today.`}
                        onSave={(text) => void setNote(store, project.id, date, person.id, text)}
                        onCancel={() => setOpenNote(null)}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {visible.length === 0 ? (
            <p className="px-4 py-6 text-ink-2">No name matches “{filter}”.</p>
          ) : null}
        </>
      )}
    </ProjectFrame>
  );
}
